import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  CircleAlert,
  Wrench,
} from "lucide-react";
import {
  getCachedServiceStatusSnapshot,
  getServiceStatusSnapshot,
  listenForServiceStatusChanges,
} from "../services/serviceStatusService";

export default function GlobalServiceStatusBanner() {
  const location = useLocation();
  const [snapshot, setSnapshot] = useState(() =>
    getCachedServiceStatusSnapshot(),
  );

  const refresh = useCallback(async (force = false) => {
    try {
      const next = await getServiceStatusSnapshot({ force });
      setSnapshot(next);
    } catch (error) {
      console.warn("Could not refresh UniAir service status:", error);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => refresh(false), 350);
    // Background tabs need not continually hit Auth + PostgREST. Recheck
    // immediately on return, rather than waiting for the next minute.
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh(true);
    }, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    const unsubscribe = listenForServiceStatusChanges(() => refresh(true));

    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      unsubscribe();
    };
  }, [refresh]);

  const state = snapshot?.overall_state || "operational";

  if (state === "operational" || location.pathname === "/status") {
    return null;
  }

  const meta =
    state === "outage"
      ? {
          icon: CircleAlert,
          wrapper: "border-red-200 bg-red-50 text-red-950",
          link: "text-red-800 hover:text-red-950",
          label: "Service outage",
        }
      : state === "maintenance"
        ? {
            icon: Wrench,
            wrapper: "border-blue-200 bg-blue-50 text-blue-950",
            link: "text-blue-800 hover:text-blue-950",
            label: "Maintenance",
          }
        : {
            icon: AlertTriangle,
            wrapper: "border-amber-200 bg-amber-50 text-amber-950",
            link: "text-amber-800 hover:text-amber-950",
            label: "Service update",
          };

  const Icon = meta.icon;

  return (
    <div className={`relative z-[70] border-b ${meta.wrapper}`} role="status">
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-2.5 sm:items-center sm:px-6">
        <Icon
          className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0"
          strokeWidth={2.2}
        />

        <div className="min-w-0 flex-1 text-sm">
          <span className="font-bold">{meta.label}:</span>{" "}
          <span className="font-medium">
            {snapshot?.overall_message ||
              "Some UniAir services are temporarily experiencing delays."}
          </span>
        </div>

        <Link
          to="/status"
          className={`inline-flex shrink-0 items-center gap-1 text-xs font-bold ${meta.link}`}
        >
          View status
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.2} />
        </Link>
      </div>
    </div>
  );
}
